import React, { useState } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { PAGE_HELP_DRAWER_WIDTH_PX } from './help/pageHelpDrawerWidth';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import Header from './Header';
import Sidebar from './Sidebar';
import Footer from './Footer';
import SinUbicacionesAsignadasAlert from './shared/SinUbicacionesAsignadasAlert';
import { UbicacionesUsuarioProvider, useUbicacionesUsuarioContext } from '../contexts/UbicacionesUsuarioContext';
import { navigationMenuItems } from '../config/navigationMenu';
import { administrationMenuItem, getAdministracionBackPath, sistemaModulePath } from '../config/adminNavigation';
import {
  readAdminBackTo,
  isValidAdminBackTarget,
  type AdminNavigationState,
} from '../utils/adminNavigationContext';

const LayoutContent: React.FC = () => {
  const { sinAsignaciones, esAdmin } = useUbicacionesUsuarioContext();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [pageHelpOpen, setPageHelpOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  // Leer el estado guardado del sidebar o usar true por defecto
  const [desktopOpen, setDesktopOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarOpen');
    return saved !== null ? saved === 'true' : true;
  });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopToggle = () => {
    const newState = !desktopOpen;
    setDesktopOpen(newState);
    // Guardar el estado en localStorage
    localStorage.setItem('sidebarOpen', String(newState));
  };

  const modulePaths = [
    administrationMenuItem.path,
    sistemaModulePath,
    ...navigationMenuItems
      .filter((item) => item.subItems && item.subItems.length > 0)
      .map((item) => item.path),
  ];

  const moduloActualPath = modulePaths.find(
    (modulePath) =>
      location.pathname === modulePath ||
      location.pathname.startsWith(`${modulePath}/`)
  );

  /** Listas de precios viven bajo /ventas pero el acceso habitual es desde Inventario → Mantenimiento precios. */
  const esListasPreciosVentas =
    /^\/ventas\/listaprecios(\/|$)/.test(location.pathname) ||
    /^\/ventas\/lista-precios(\/|$)/.test(location.pathname);

  const destinoAdministracion = getAdministracionBackPath(
    location.pathname,
    [((location.state as AdminNavigationState | null)?.adminBackTo), readAdminBackTo()].find(
      (path) => path && isValidAdminBackTarget(path),
    ) ?? null,
  );

  const destinoVolverMenuRaw = esListasPreciosVentas
    ? '/inventario'
    : destinoAdministracion ?? moduloActualPath;

  const destinoVolverMenu =
    destinoVolverMenuRaw && destinoVolverMenuRaw !== location.pathname
      ? destinoVolverMenuRaw
      : moduloActualPath && moduloActualPath !== location.pathname
        ? moduloActualPath
        : destinoVolverMenuRaw;

  const mostrarBarraVolverModulo = Boolean(
    moduloActualPath && location.pathname !== moduloActualPath
  );

  return (
    <Box className="app-shell" sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column' }}>
      <Box className="app-content-row" sx={{ display: 'flex', flexGrow: 1 }}>
        <Header
          onMenuClick={handleDrawerToggle}
          desktopOpen={desktopOpen}
          onDesktopToggle={handleDesktopToggle}
          pageHelpOpen={pageHelpOpen}
          onPageHelpOpenChange={setPageHelpOpen}
        />
        <Sidebar 
          mobileOpen={mobileOpen} 
          onMobileClose={handleDrawerToggle}
          desktopOpen={desktopOpen}
          onDesktopToggle={handleDesktopToggle}
        />
        <Box
          className="app-main"
          component="main"
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            pl: { xs: 2, sm: 3 },
            pr: pageHelpOpen
              ? {
                  xs: 2,
                  sm: (t) =>
                    `calc(${t.spacing(3)} + ${PAGE_HELP_DRAWER_WIDTH_PX.sm}px)`,
                  md: (t) =>
                    `calc(${t.spacing(3)} + ${PAGE_HELP_DRAWER_WIDTH_PX.md}px)`,
                }
              : { xs: 2, sm: 3 },
            pb: { xs: 2, sm: 3 },
            pt: mostrarBarraVolverModulo ? 0 : { xs: 2, sm: 3 },
            mt: 8, // Para dejar espacio para el header
            backgroundColor: '#f5f5f5',
            overflow: 'auto',
            width: { xs: '100%', md: desktopOpen ? `calc(100% - 280px)` : `calc(100% - 72px)` },
            transition: (t) =>
              t.transitions.create(['width', 'padding-right'], {
                easing: t.transitions.easing.sharp,
                duration: t.transitions.duration.enteringScreen,
              }),
          }}
        >
          <Box className="app-outlet-wrap" sx={{ flexGrow: 1 }}>
            {mostrarBarraVolverModulo && moduloActualPath && destinoVolverMenu && (
              <Box
                className="app-back-to-module"
                sx={{
                  mb: 0,
                  mt: 0,
                  display: 'flex',
                  alignItems: 'center',
                  pt: 0,
                  pl: { xs: 2, sm: 3 },
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    transform: { xs: 'translateY(12px)', sm: 'translateY(14px)' },
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  <Button
                    variant="contained"
                    startIcon={<ArrowBack />}
                    onClick={() => navigate(destinoVolverMenu)}
                    sx={{ textTransform: 'none' }}
                  >
                    Volver al Menu
                  </Button>
                </Stack>
              </Box>
            )}
            {sinAsignaciones && <SinUbicacionesAsignadasAlert esAdmin={esAdmin} />}
            <Outlet />
          </Box>
          <Footer />
        </Box>
      </Box>
    </Box>
  );
};

const Layout: React.FC = () => (
  <UbicacionesUsuarioProvider>
    <LayoutContent />
  </UbicacionesUsuarioProvider>
);

export default Layout; 